import { useState } from "react";
import { parseCSV, rowsToItems } from "../vault/csv";
import { createItem, type Field, type ItemContent } from "../vault/items";
import { parseRdm, parseRdmCsv, applyCsvCreds, type RdmParsed } from "../vault/rdm";
import { parseMRemoteNG, MrngPasswordRequired, type MrngParsed } from "../vault/mremoteng";
import { pathDepth } from "../vault/importTree";
import { api } from "../api/client";
import { b64encode, encryptName, encryptMeta } from "../crypto/vault";
import { useT } from "../i18n";

// A normalized item ready to encrypt + create, produced by either the CSV or kdbx parser.
type Pending = { type: string; content: ItemContent };

// Outcome of an import run, so the caller can report partial failures instead of a blanket
// "Import complete" that hides silently-skipped rows.
export type ImportResult = { imported: number; failed: number; firstError?: string };

// Client-side import (CSV or KeePass .kdbx): parse + decrypt locally, then encrypt +
// create each item in the browser (§3/§4). Plaintext never leaves the device as plaintext.
export function ImportDialog({
  orgId,
  orgKey,
  onClose,
  onDone,
}: {
  orgId: string;
  orgKey: Uint8Array;
  onClose: () => void;
  onDone: (result?: ImportResult) => void;
}) {
  const t = useT();
  const [items, setItems] = useState<Pending[]>([]);
  const [rdm, setRdm] = useState<RdmParsed | null>(null);
  const [credMatch, setCredMatch] = useState<{ matched: number; total: number } | null>(null);
  const [mrng, setMrng] = useState<MrngParsed | null>(null);
  const [mrngText, setMrngText] = useState<string | null>(null); // XML awaiting a master password
  const [mrngPw, setMrngPw] = useState("");
  const [kdbxBuf, setKdbxBuf] = useState<ArrayBuffer | null>(null);
  const [kdbxPw, setKdbxPw] = useState("");
  const [progress, setProgress] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setItems([]);
    setRdm(null);
    setCredMatch(null);
    setKdbxBuf(null);
    setMrng(null);
    setMrngText(null);
    setMrngPw("");
    const lower = file.name.toLowerCase();
    if (lower.endsWith(".kdbx")) {
      setKdbxBuf(await file.arrayBuffer());
      return; // wait for the master password
    }
    if (lower.endsWith(".json")) {
      try {
        setRdm(parseRdm(await file.text()));
      } catch {
        setError(t("Could not read this RDM export (expected a Devolutions RDM .json)."));
      }
      return;
    }
    if (lower.endsWith(".xml")) {
      const text = await file.text();
      try {
        setMrng(await parseMRemoteNG(text)); // tries mRemoteNG's default master password first
      } catch (e) {
        if (e instanceof MrngPasswordRequired) {
          setMrngText(text); // custom password: prompt for it (see parseMrngFile)
        } else {
          setError(t("Could not read this mRemoteNG export (expected a confCons .xml)."));
        }
      }
      return;
    }
    try {
      const parsed = rowsToItems(parseCSV(await file.text())).map<Pending>((it) => ({
        type: "login",
        content: {
          name: it.name,
          notes: it.notes,
          fields: [
            it.url ? f("url", "URL", "url", it.url) : null,
            it.username ? f("username", "Username", "username", it.username) : null,
            it.password ? f("password", "Password", "password", it.password, true) : null,
          ].filter((x): x is Field => x !== null),
        },
      }));
      setItems(parsed);
      if (parsed.length === 0) setError(t("No rows found (need a header row with a name/title column)."));
    } catch {
      setError(t("Could not read the file"));
    }
  }

  // Optional second file for the RDM path: the CSV export with credentials (passwords in clear).
  // Merge it onto the parsed accesses by RDM ID, so the device tree (JSON) gains usernames/passwords.
  async function onCredCsv(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !rdm) return;
    setError(null);
    try {
      const creds = parseRdmCsv(await file.text());
      const result = applyCsvCreds(rdm, creds);
      setRdm({ ...rdm });
      setCredMatch(result);
    } catch {
      setError(t("Could not read the credentials CSV."));
    }
  }

  async function parseKdbxFile() {
    if (!kdbxBuf) return;
    setBusy(true);
    setError(null);
    try {
      const { parseKdbx } = await import("../vault/kdbx"); // lazy: pulls in kdbxweb + hash-wasm only on demand
      const entries = await parseKdbx(kdbxBuf, kdbxPw);
      const parsed = entries.map<Pending>((e) => ({
        type: "login",
        content: {
          name: e.name,
          notes: e.notes,
          tags: e.group ? [e.group] : [],
          fields: [
            e.url ? f("url", "URL", "url", e.url) : null,
            e.username ? f("username", "Username", "username", e.username) : null,
            e.password ? f("password", "Password", "password", e.password, true) : null,
            // Store any imported TOTP seed as a plain secret (live generator deferred to a later phase).
            e.totp ? f("totp_seed_imported", "TOTP seed (imported)", "secret", e.totp, true) : null,
            ...e.extra.map((x) => f(slug(x.label), x.label, x.secret ? "secret" : "text", x.value, x.secret)),
          ].filter((x): x is Field => x !== null),
        },
      }));
      setItems(parsed);
      if (parsed.length === 0) setError(t("No entries found in this database."));
    } catch {
      setError(t("Could not open the database — wrong password or unsupported format."));
    } finally {
      setBusy(false);
    }
  }

  // mRemoteNG with a custom master password: re-parse the held XML with the entered password.
  async function parseMrngFile() {
    if (!mrngText) return;
    setBusy(true);
    setError(null);
    try {
      setMrng(await parseMRemoteNG(mrngText, mrngPw));
      setMrngText(null);
    } catch (e) {
      setError(e instanceof MrngPasswordRequired ? t("Wrong master password.") : t("Could not read this mRemoteNG export (expected a confCons .xml)."));
    } finally {
      setBusy(false);
    }
  }

  async function run() {
    if (rdm) return runRdm(rdm);
    if (mrng) return runRdm(mrng);
    setProgress(0);
    let imported = 0;
    let failed = 0;
    let firstError: string | undefined;
    for (let i = 0; i < items.length; i++) {
      try {
        await createItem(orgId, orgKey, items[i].type, items[i].content);
        imported++;
      } catch (e) {
        failed++;
        if (!firstError) firstError = e instanceof Error ? e.message : String(e);
      }
      setProgress(i + 1);
    }
    onDone({ imported, failed, firstError });
  }

  // Create the RDM tree: nodes (folders + device nodes) shallow-first so parents exist before
  // children, then the access items under their node. Failures skip and continue.
  async function runRdm(parsed: RdmParsed) {
    setProgress(0);
    let done = 0;
    let imported = 0;
    let failed = 0;
    let firstError: string | undefined;
    const recordFailure = (e: unknown) => {
      failed++;
      if (!firstError) firstError = e instanceof Error ? e.message : String(e);
    };
    const idByPath = new Map<string, string>();
    const ordered = [...parsed.nodes].sort((a, b) => pathDepth(a.path) - pathDepth(b.path));
    for (const n of ordered) {
      try {
        const parentId = n.parentPath ? idByPath.get(n.parentPath) : undefined;
        const encName = b64encode(encryptName(orgKey, n.name));
        const encMeta = n.kind === "device" && n.meta ? b64encode(encryptMeta(orgKey, n.meta)) : undefined;
        const folder = await api.createFolder(orgId, encName, parentId, n.kind, encMeta);
        idByPath.set(n.path, folder.id);
        imported++;
      } catch (e) {
        recordFailure(e);
      }
      setProgress(++done);
    }
    for (const a of parsed.accesses) {
      try {
        const content: ItemContent = { name: a.name, notes: a.notes, fields: a.fields };
        await createItem(orgId, orgKey, a.type, content, idByPath.get(a.groupPath));
        imported++;
      } catch (e) {
        recordFailure(e);
      }
      setProgress(++done);
    }
    onDone({ imported, failed, firstError });
  }

  const tree = rdm ?? mrng;
  const total = tree ? tree.nodes.length + tree.accesses.length : items.length;
  const showKdbxPw = kdbxBuf !== null && items.length === 0;
  const showMrngPw = mrngText !== null;

  return (
    <div className="fixed inset-0 grid place-items-center bg-black/50" onClick={onClose}>
      <div className="w-[440px] rounded-md border border-border bg-panel shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border px-4 py-3 font-semibold">{t("Import")}</div>
        <div className="flex flex-col gap-3 px-4 py-4 text-sm">
          <p className="text-xs text-muted">
            {t("Import from a CSV (KeePass/Bitwarden columns), a KeePass .kdbx database, a Devolutions RDM .json export, or an mRemoteNG .xml export. Parsing and encryption happen in your browser.")}
          </p>
          <input type="file" accept=".csv,text/csv,.kdbx,.json,application/json,.xml,text/xml,application/xml" onChange={onFile} className="text-xs" />
          {showKdbxPw && (
            <div className="flex flex-col gap-2">
              <input
                type="password"
                placeholder={t("Master password")}
                value={kdbxPw}
                onChange={(e) => setKdbxPw(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && parseKdbxFile()}
                className="rounded border border-border bg-panel2 px-2 py-1.5 text-xs outline-none focus:border-accent"
                autoFocus
              />
              <button
                onClick={parseKdbxFile}
                disabled={busy || !kdbxPw}
                className="self-start rounded bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent2 disabled:opacity-60"
              >
                {busy ? "…" : t("Open database")}
              </button>
            </div>
          )}
          {showMrngPw && (
            <div className="flex flex-col gap-2">
              <p className="text-xs text-muted">{t("This mRemoteNG file uses a custom master password.")}</p>
              <input
                type="password"
                placeholder={t("Master password")}
                value={mrngPw}
                onChange={(e) => setMrngPw(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && parseMrngFile()}
                className="rounded border border-border bg-panel2 px-2 py-1.5 text-xs outline-none focus:border-accent"
                autoFocus
              />
              <button
                onClick={parseMrngFile}
                disabled={busy || !mrngPw}
                className="self-start rounded bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent2 disabled:opacity-60"
              >
                {busy ? "…" : t("Decrypt")}
              </button>
            </div>
          )}
          {items.length > 0 && <div className="text-xs text-muted">{items.length} {t("items")} · {t("ready to import")}</div>}
          {mrng && (
            <div className="text-xs text-muted">
              {mrng.counts.folders} {t("folders")} · {mrng.counts.accesses} {t("connections")} · {t("ready to import")}
            </div>
          )}
          {rdm && (
            <div className="flex flex-col gap-2">
              <div className="text-xs text-muted">
                {rdm.counts.folders} {t("folders")} · {rdm.counts.devices} {t("devices")} · {rdm.counts.accesses} {t("access methods")} · {t("ready to import")}
              </div>
              <label className="text-xs text-muted">
                {t("Optional: add the RDM CSV export (with credentials) to fill in usernames/passwords.")}
              </label>
              <input type="file" accept=".csv,text/csv" onChange={onCredCsv} className="text-xs" />
              {credMatch && (
                <div className="text-xs text-accent">
                  {t("Credentials matched")}: {credMatch.matched} / {credMatch.total}
                </div>
              )}
            </div>
          )}
          {progress !== null && (
            <div className="text-xs text-accent">
              {t("Imported")} {progress} / {total}…
            </div>
          )}
          {error && <div className="text-xs text-danger">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
          <button onClick={onClose} className="rounded border border-border px-3 py-1.5 hover:border-accent">
            {t("Cancel")}
          </button>
          <button
            onClick={run}
            disabled={total === 0 || progress !== null}
            className="rounded bg-accent px-3 py-1.5 font-medium text-white hover:bg-accent2 disabled:opacity-60"
          >
            {t("Import")} {total || ""}
          </button>
        </div>
      </div>
    </div>
  );
}

function f(key: string, label: string, type: Field["type"], value: string, secret = false): Field {
  return { key, label, type, value, secret };
}
function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") || "field";
}
